// Statistiques des réclamations : RCL-01 (reçues), RCL-02 (taux répondues dans le délai), RCL-03
// (satisfaction moyenne) avec, pour chacune, la liste explicative servie par /indicateurs/liste avec
// les mêmes filtres que la valeur (R16). Une ligne masquée (réclamation sensible sans habilitation)
// est comptée mais sans lien.
import { Filtres, Lien, LienBouton, Panneau, Squelette, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { EnTetePage } from '../../commun/EnTetePage';
import { AffichageErreur } from '../../commun/EtatRequete';
import { fraicheur } from '../../commun/format';
import { tuile } from '../tableau/catalogue';
import { libelleUnite, useListeIndicateur, useValeurIndicateur, type FiltresTableau, type LigneExplicative } from '../tableau/donnees';
import { calculerPeriode, type CodePeriode } from '../tableau/periode';
import commun from '../../commun/commun.module.css';
import styles from './reclamations.module.css';

const CODES = ['RCL-01', 'RCL-02', 'RCL-03'] as const;

/** Message de couverture (« non activé », « non renseigné ») quand l'API le fournit. */
function messageCouverture(couverture: unknown): string {
  if (typeof couverture === 'object' && couverture !== null && 'message' in couverture && typeof (couverture as { message: unknown }).message === 'string') {
    return ` ; ${(couverture as { message: string }).message}`;
  }
  return '';
}

const COLONNES: ColonneTableau<LigneExplicative>[] = [
  { id: 'reference', entete: 'Numéro', enTeteLigne: true, masquable: false, cellule: (l) => (l.href ? <Lien href={l.href}>{l.reference}</Lien> : l.reference) },
  { id: 'objet', entete: 'Catégorie ou objet', cellule: (l) => l.objet },
  { id: 'detail', entete: 'Précision', cellule: (l) => l.detail ?? 'non renseigné' },
  { id: 'etat', entete: 'État ou mesure', cellule: (l) => l.etat ?? 'non renseigné' },
];

function Indicateur({ code, filtres }: { code: (typeof CODES)[number]; filtres: FiltresTableau }) {
  const definition = tuile(code);
  const valeur = useValeurIndicateur(definition ?? { code, libelle: code, description: '', source: 'indicateur', droit: null, ton: 'bleu' }, filtres, true);
  const liste = useListeIndicateur(definition, filtres, true);
  if (!definition) return null;
  return (
    <Panneau titre={`${code} : ${definition.libelle}`}>
      <p className={commun.na}>{definition.description}.</p>
      {valeur.isPending ? <Squelette hauteurBloc={40} /> : valeur.error ? <AffichageErreur erreur={valeur.error} lot="L1-A" fonction="L'indicateur" /> : (
        <p className={styles.valeur} data-testid={`valeur-${code}`}>
          {valeur.data.valeur === null ? 'Non renseigné' : `${valeur.data.valeur.toLocaleString('fr-FR')}${libelleUnite(valeur.data.unite)}`}
          <span className={commun.na}> calculé {fraicheur(valeur.data.calcule_le)}{messageCouverture(valeur.data.couverture)}</span>
        </p>
      )}
      {liste.isPending ? <Squelette lignes={3} /> : liste.error ? <AffichageErreur erreur={liste.error} lot="L1-A" fonction="La liste explicative" /> : liste.data.lignes.length === 0 ? <p className={commun.na}>Aucune ligne pour cette période.</p> : (
        <>
          <p role="status">
            {liste.data.lignes.length} ligne{liste.data.lignes.length > 1 ? 's' : ''}
            {liste.data.lignes.some((l) => l.href === undefined) ? ' (les réclamations sensibles hors de votre habilitation sont comptées sans être nommées)' : ''}
          </p>
          <Tableau legende={`Liste explicative ${code}`} colonnes={COLONNES} lignes={liste.data.lignes} cleLigne={(l) => l.id} />
        </>
      )}
    </Panneau>
  );
}

export function PageStatistiquesReclamations() {
  const [valeurs, setValeurs] = useFiltresPersistes('reclamations-statistiques', { periode: 'mois' });
  const filtres: FiltresTableau = { periode: (valeurs.periode as CodePeriode) ?? 'mois', arrondissement: '' };
  const periode = calculerPeriode(filtres.periode);
  return (
    <>
      <EnTetePage etiquette="Réclamations et satisfaction" titre="Statistiques des réclamations" description={`Période : ${periode.libelle}, du ${periode.debut} au ${periode.fin}. Chaque valeur est égale au nombre de lignes de sa liste explicative, calculée avec les mêmes filtres.`} actions={<LienBouton href="/reclamations" icone={ArrowLeft}>Réclamations</LienBouton>} />
      <Filtres
        libelle="Période"
        groupes={[{ id: 'periode', libelle: 'Période', options: [{ valeur: 'mois', libelle: 'Ce mois' }, { valeur: 'trimestre', libelle: 'Trimestre' }, { valeur: 'exercice', libelle: 'Exercice' }] }]}
        valeurs={valeurs}
        onChanger={setValeurs}
      />
      <div className={commun.pile}>
        {CODES.map((c) => (
          <Indicateur key={c} code={c} filtres={filtres} />
        ))}
      </div>
    </>
  );
}
